"use client";
import { useEffect } from "react";
import { CheckCircle, X, AlertCircle, Info } from "lucide-react";

interface ToastProps {
  message: string;
  duration?: number;
  onClose: () => void;
  type?: "success" | "error" | "info" | "warning";
}

export default function Toast({
  message,
  duration = 3000,
  onClose,
  type = "success",
}: ToastProps) {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const getToastStyles = () => {
    switch (type) {
      case "success":
        return "bg-white border-l-4 border-leaf-green shadow-lg";
      case "error":
        return "bg-white border-l-4 border-red-500 shadow-lg";
      case "warning":
        return "bg-white border-l-4 border-orange-500 shadow-lg";
      case "info":
      default:
        return "bg-white border-l-4 border-coral-splash shadow-lg";
    }
  };

  const getIcon = () => {
    const iconClass = "h-5 w-5 flex-shrink-0";
    switch (type) {
      case "success":
        return <CheckCircle className={`${iconClass} text-leaf-green`} />;
      case "error":
        return <X className={`${iconClass} text-red-500`} />;
      case "warning":
        return <AlertCircle className={`${iconClass} text-orange-500`} />;
      case "info":
      default:
        return <Info className={`${iconClass} text-coral-splash`} />;
    }
  };

  const getTextColor = () => {
    switch (type) {
      case "success":
        return "text-leaf-green";
      case "error":
        return "text-red-700";
      case "warning":
        return "text-orange-700";
      case "info":
      default:
        return "text-coral-splash";
    }
  };

  return (
    <div
      className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-bottom-2 duration-300 max-w-sm w-full mx-4"
      role="alert"
      aria-live="assertive"
    >
      <div className={`flex items-start gap-3 p-4 rounded-xl backdrop-blur-sm ${getToastStyles()}`}>
        {getIcon()}
        <div className="flex-1 min-w-0">
          <p className={`font-medium text-sm ${getTextColor()}`}>{message}</p>
        </div>
        <button
          onClick={onClose}
          className="flex-shrink-0 p-1 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Close notification"
        >
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>
    </div>
  );
}